import React, { useState } from 'react';
import './MetadataTree.css';
import { List, Copy, Check, ChevronRight, ChevronDown, FileJson, AlertCircle, Filter } from 'lucide-react';

function MetadataTree({ metadata, searchTerm, aiDetection }) {
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  // Build list of trigger keywords from AI detection
  const getTriggerKeywords = () => {
    const keywords = [];
    if (!aiDetection) return keywords;

    // Get generator name
    if (aiDetection.generator || aiDetection.aiGenerator) {
      const gen = (aiDetection.generator || aiDetection.aiGenerator || '').toLowerCase();
      keywords.push(gen);
      // Also add parts of the generator name
      gen.split(/[\s-_]/).forEach(part => {
        if (part.length > 3) keywords.push(part);
      });
    }

    // Get evidence strings
    if (aiDetection.evidence) {
      aiDetection.evidence.forEach(e => {
        // Extract key parts from evidence
        const match = e.match(/^([^:]+):/);
        if (match) keywords.push(match[1].toLowerCase());
      });
    }

    // Common AI-related keywords to highlight
    const aiKeywords = [
      'encoder', 'software', 'creator', 'generator', 'handler', 'tool',
      'upscale', 'denoise', 'esrgan', 'topaz', 'comfyui', 'stable diffusion',
      'midjourney', 'dalle', 'runway', 'kling', 'pika', 'luma',
      'prompt', 'model', 'sampler', 'cfg', 'steps',
      'ai', 'neural', 'machine learning'
    ];

    keywords.push(...aiKeywords);

    return [...new Set(keywords)]; // Remove duplicates
  };

  const triggerKeywords = getTriggerKeywords();

  const isTriggered = (key, value) => {
    if (!aiDetection?.isAIGenerated) return false;

    const keyLower = key.toLowerCase();
    const valueLower = String(value).toLowerCase();

    // Check if this key or value matches any trigger keywords
    return triggerKeywords.some(keyword =>
      keyLower.includes(keyword) || valueLower.includes(keyword)
    );
  };

  // Filter out AI detection fields - only show actual file metadata
  const AI_DETECTION_FIELDS = [
    'isAIGenerated', 'aiGenerator', 'aiPrompt', 'confidence',
    'evidence', 'deepScanResults', 'c2paVerified', 'iptcVerified',
    'aiType', 'enhancementTool'
  ];

  const filterMetadata = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const filtered = {};
    for (const [key, value] of Object.entries(obj)) {
      if (!AI_DETECTION_FIELDS.includes(key)) {
        filtered[key] = typeof value === 'object' && value !== null && !Array.isArray(value)
          ? filterMetadata(value)
          : value;
      }
    }
    return filtered;
  };

  const pureMetadata = filterMetadata(metadata);

  const handleCopy = () => {
    if (pureMetadata) {
      const formatTree = (obj, indent = '') => {
        let result = '';
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'object' && value !== null) {
            result += `${indent}${key}:\n${formatTree(value, indent + '  ')}`;
          } else {
            result += `${indent}${key}: ${value}\n`;
          }
        }
        return result;
      };
      navigator.clipboard.writeText(formatTree(pureMetadata));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleCollapse = (key) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Count flagged items
  const countFlagged = (obj) => {
    if (!obj || typeof obj !== 'object') return 0;
    let count = 0;
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null) {
        count += countFlagged(value);
      } else if (isTriggered(key, value)) {
        count++;
      }
    }
    return count;
  };

  const flaggedCount = countFlagged(pureMetadata);

  if (!metadata) {
    return (
      <div className="metadata-tree-container">
        <div className="panel-header">
          <h2><List size={16} /> Metadata Tree</h2>
        </div>
        <div className="empty-state">
          <FileJson size={32} className="empty-icon" />
          <p className="no-data">No metadata available</p>
        </div>
      </div>
    );
  }

  const renderTree = (obj, prefix = '', depth = 0) => {
    if (!obj || typeof obj !== 'object') return null;
    const entries = Object.entries(obj);

    return entries.map(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const searchMatch =
        !searchTerm ||
        fullKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(value).toLowerCase().includes(searchTerm.toLowerCase());
      if (!searchMatch) return null;

      const isObject = typeof value === 'object' && value !== null;
      const isCollapsed = collapsed[fullKey];
      const triggered = !isObject && isTriggered(key, value);

      // If showing flagged only, skip non-triggered leaves
      if (showFlaggedOnly && !isObject && !triggered) return null;

      if (isObject) {
        // Check if any child is triggered
        const hasTriggeredChild = countFlagged(value) > 0;

        // If showing flagged only, skip branches with no triggered children
        if (showFlaggedOnly && !hasTriggeredChild) return null;

        const childCount = Object.keys(value).length;

        return (
          <li key={fullKey} className={`tree-branch ${hasTriggeredChild ? 'has-trigger' : ''}`}>
            <div
              className="tree-branch-header"
              onClick={() => toggleCollapse(fullKey)}
            >
              <span className="tree-collapse-icon">
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </span>
              <span className="tree-key">{key}</span>
              <span className="tree-count">{childCount}</span>
              {hasTriggeredChild && <AlertCircle size={12} className="trigger-indicator" />}
            </div>
            {!isCollapsed && (
              <ul className="tree-children">{renderTree(value, fullKey, depth + 1)}</ul>
            )}
          </li>
        );
      }

      return (
        <li key={fullKey} className={`tree-leaf ${triggered ? 'triggered' : ''}`}>
          {triggered && <AlertCircle size={12} className="trigger-icon" />}
          <span className="tree-key">{key}</span>
          <span className="tree-separator">:</span>
          <span className="tree-value" title={String(value)}>
            {String(value).length > 50 ? String(value).substring(0, 50) + '...' : String(value)}
          </span>
        </li>
      );
    });
  };

  return (
    <div className="metadata-tree-container">
      <div className="panel-header">
        <h2><List size={16} /> Metadata Tree</h2>
        <button className="copy-btn" onClick={handleCopy}>
          {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
        </button>
      </div>

      {aiDetection?.isAIGenerated && flaggedCount > 0 && (
        <div className="filter-bar">
          <button
            className={`filter-btn ${showFlaggedOnly ? 'active' : ''}`}
            onClick={() => setShowFlaggedOnly(!showFlaggedOnly)}
          >
            <Filter size={12} />
            {showFlaggedOnly ? 'Show All' : `Show Flagged (${flaggedCount})`}
          </button>
          <div className="trigger-legend">
            <AlertCircle size={12} />
            <span>AI trigger</span>
          </div>
        </div>
      )}

      <ul className="tree-list">{renderTree(pureMetadata)}</ul>
    </div>
  );
}

export default MetadataTree;